/**
 * Paso 0 de la activación (T033): bienvenida de `/activar/{token}`.
 *
 * Muestra solo el **nombre de pila** del trabajador y el nombre comercial de la empresa: el enlace
 * y esta pantalla nunca exponen documento, teléfono ni email (FR-006). 005 · QA-6: se llega aquí
 * solo tras verificar el documento; el nombre sale de esa respuesta (si faltara, saludo genérico).
 *
 * También vive aquí la variante de escritorio: si `detectPlatform() === 'desktop'` la activación no
 * continúa (la credencial debe crearse en el teléfono que marcará) y se ofrece el enlace copiable.
 */
import { ArrowDown, Copy, Smartphone } from 'lucide-react';
import { useState } from 'react';

import { type ActivationInfo } from '@fermin/contracts';

import { Button } from '../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../components/ui/card';
import { Screen } from '../../components/ui/screen';
import { formatDateTime, t } from '../../i18n';

export interface ActivationWelcomeProps {
  info: ActivationInfo;
  onContinue: () => void;
}

export function ActivationWelcome({ info, onContinue }: ActivationWelcomeProps) {
  return (
    <Screen
      data-testid="activation-welcome"
      title={
        info.worker_first_name
          ? t('activation.welcome.title', { name: info.worker_first_name })
          : t('activation.welcome.titleNoName')
      }
      lead={t('activation.welcome.lead', { company: info.company_trade_name })}
      footer={
        <>
          {/* Piloto 2026-09-30: la gente no entendía que debía tocar el botón; la indicación lo
              nombra y queda pegada a él, dentro del pie fijo. */}
          <p
            id="activation-welcome-hint"
            data-testid="activation-welcome-hint"
            className="flex items-center justify-center gap-fm-2 text-center text-fm-base text-fm-text"
          >
            <ArrowDown aria-hidden="true" size={18} className="shrink-0 text-fm-text-secondary" />
            <span>
              {t('activation.welcome.hintBefore')}{' '}
              <strong className="font-bold">{t('common.buttons.continue')}</strong>{' '}
              {t('activation.welcome.hintAfter')}
            </span>
          </p>
          <Button onClick={onContinue} aria-describedby="activation-welcome-hint">
            {t('common.buttons.continue')}
          </Button>
        </>
      }
    >
      <Card>
        <CardTitle>{info.company_trade_name}</CardTitle>
        <CardDescription>
          {t('activation.welcome.expires', { date: formatDateTime(info.expires_at) })}
        </CardDescription>
      </Card>
    </Screen>
  );
}

export interface DesktopNoticeProps {
  /** Enlace de activación tal como lo abrió la persona (se muestra para copiarlo al teléfono). */
  link: string;
}

/** Escritorio: no se registra ningún dispositivo y no hay botón para continuar (spec, casos límite). */
export function DesktopNotice({ link }: DesktopNoticeProps) {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(link);
      setCopied(true);
    } catch {
      // Sin permiso de portapapeles: el enlace sigue visible y se puede seleccionar a mano.
      setCopied(false);
    }
  }

  return (
    <Screen
      data-testid="activation-desktop"
      align="center"
      title={t('activation.desktop.title')}
      lead={t('activation.desktop.description')}
    >
      <Card>
        <CardTitle>
          <span className="inline-flex items-center gap-fm-2">
            <Smartphone aria-hidden="true" size={20} />
            {t('activation.desktop.linkLabel')}
          </span>
        </CardTitle>
        <p className="mt-fm-3 break-all rounded-fm-md bg-fm-bg-sunken p-fm-3 text-fm-sm text-fm-text">
          {link}
        </p>
        <div className="mt-fm-3">
          <Button
            variant="secondary"
            fullWidth={false}
            icon={<Copy aria-hidden="true" size={20} />}
            onClick={() => void copy()}
          >
            {copied ? t('common.buttons.copied') : t('common.buttons.copy')}
          </Button>
        </div>
      </Card>
    </Screen>
  );
}
